import { useEffect, useState } from 'react';

const LATER_KEY = 'pwa_install_later_until';
const LATER_COOLDOWN = 3 * 24 * 60 * 60 * 1000; // 3 days

export default function PwaControls() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [installed, setInstalled] = useState(
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  );

  useEffect(() => {
    const onInstallPrompt = event => {
      event.preventDefault();
      const later = Number(localStorage.getItem(LATER_KEY) || 0);
      if (Date.now() < later) return;
      setInstallPrompt(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/sw.js`).then(registration => {
        if (registration.waiting) setWaitingWorker(registration.waiting);
        registration.addEventListener('updatefound', () => {
          const worker = registration.installing;
          worker?.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) {
              setWaitingWorker(worker);
            }
          });
        });
        registration.update().catch(() => {});
      }).catch(() => {});
    }

    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker?.addEventListener('controllerchange', onControllerChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      navigator.serviceWorker?.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const dismissForNow = () => {
    // Only the install prompt gets a cooldown — an available update should
    // keep offering itself next visit rather than staying hidden for days.
    if (!waitingWorker) {
      try { localStorage.setItem(LATER_KEY, String(Date.now() + LATER_COOLDOWN)); } catch {}
    }
    setInstallPrompt(null);
  };

  const update = () => waitingWorker?.postMessage({ type: 'SKIP_WAITING' });

  if (!waitingWorker && (!installPrompt || installed)) return null;

  const features = [
    { icon: '⚡', label: 'Works offline' },
    { icon: '🚀', label: 'Fast loading' },
    { icon: '📱', label: 'Native app experience' },
  ];

  return (
    <div className="pwa-controls-overlay">
    <div className="pwa-controls" role="dialog" aria-modal="true" aria-label="Install Deriv Commission">
      <button
        type="button"
        className="pwa-controls-close"
        onClick={dismissForNow}
        aria-label="Close install prompt"
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
      <div className="pwa-controls-head">
        <span className="pwa-controls-mark">
          <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="7" y="2" width="10" height="20" rx="2" />
            <path d="M11 18h2" />
          </svg>
        </span>
        <h2 className="pwa-controls-title">
          {waitingWorker ? 'Update Dev Dashboard' : 'Install Dev Dashboard'}
        </h2>
        <p className="pwa-controls-desc">
          {waitingWorker
            ? 'Get the latest improvements and bug fixes for your dashboard.'
            : 'Get quick access to your dashboard and enjoy a native app experience with offline capabilities.'}
        </p>
      </div>

      {!waitingWorker && (
        <ul className="pwa-controls-features">
          {features.map(f => (
            <li key={f.label}>
              <span className="pwa-controls-feature-icon">{f.icon}</span>
              <span>{f.label}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="pwa-controls-actions">
        <button className="pwa-btn pwa-btn-primary" onClick={waitingWorker ? update : install}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3v12" />
            <path d="m7 10 5 5 5-5" />
            <path d="M5 21h14a2 2 0 0 0 2-2v-3" />
          </svg>
          {waitingWorker ? 'Update app' : 'Install App'}
        </button>
        <button className="pwa-btn pwa-btn-ghost" onClick={dismissForNow}>
          Later
        </button>
      </div>

      <p className="pwa-controls-foot">
        You can always install this app from your browser menu
      </p>
    </div>
    </div>
  );
}
